/**
 * Inline external stylesheets and strip resources that can't run under our CSP, so
 * the imported document becomes a single self-contained file ready for in-place
 * structured edits.
 *
 *   1. The iframe CSP only allows `script-src ... https://cdn.tailwindcss.com` and
 *      `style-src ... https://fonts.googleapis.com`. Anything else loaded by the
 *      browser silently fails at render — confusing, unstyled designs.
 *   2. Relative-path `<link href="./styles.css">` cannot resolve at all in the
 *      sandboxed iframe (no same-origin, no base path). Better to remove them.
 *   3. Editing one file is easier than editing N. Once everything is inlined, the
 *      structured `<edits>` ops from the model target a single document instead of
 *      asking the user to keep multiple files in sync.
 *
 * Implementation note: uses targeted regex replacement rather than DOMParser. We
 * specifically *don't* want the browser to instantiate `<link>` / `<script>` nodes
 * during the inlining pass — both DOM environments (happy-dom in tests, real
 * Chromium at runtime) will side-effect on connect by trying to fetch the
 * resource. Regex keeps the operation local and predictable.
 */

/** Hosts the iframe CSP allows directly — leave as `<script src=...>`. */
const CSP_ALLOWED_SCRIPT_HOSTS = ['cdn.tailwindcss.com'];
/** Hosts the iframe CSP allows directly — leave as `<link rel="stylesheet" href=...>`. */
const ALLOWED_STYLESHEET_HOSTS = ['fonts.googleapis.com'];

/**
 * Trusted public-asset CDNs we'll fetch+inline so the design's libraries (three.js,
 * GSAP, Alpine, htmx, etc.) keep working even though the iframe's `script-src` is
 * locked to Tailwind. These hosts all serve `Access-Control-Allow-Origin: *` for
 * library files. Hosts NOT on this list still get stripped (analytics, trackers,
 * Cloudflare auto-injected scripts) — those are not part of "the design."
 */
const INLINEABLE_SCRIPT_HOSTS = [
  'cdnjs.cloudflare.com',
  'cdn.jsdelivr.net',
  'unpkg.com',
  'esm.sh',
  'code.jquery.com',
  'ajax.googleapis.com',
];

export interface SelfContainResult {
  html: string;
  /** External stylesheets we successfully fetched + inlined as `<style>`. */
  inlined: string[];
  /** Resources we removed entirely (relative paths, CORS failures, disallowed scripts). */
  stripped: Array<{ kind: 'link' | 'script'; href: string; reason: string }>;
  /** Resources we left as-is because the CSP allowlist permits them. */
  kept: string[];
}

const LINK_TAG_RE = /<link\b([^>]*?)\/?>/gi;
const SCRIPT_TAG_RE = /<script\b([^>]*?)>([\s\S]*?)<\/script\s*>/gi;
const SCRIPT_SELF_CLOSING_RE = /<script\b([^>]*?)\/>/gi;
const ATTR_RE = /\b([A-Za-z_:][\w:.-]*)\s*=\s*"([^"]*)"/g;

function parseAttrs(attrSrc: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = new RegExp(ATTR_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(attrSrc)) !== null) {
    out[m[1]!.toLowerCase()] = m[2]!;
  }
  return out;
}

function isAbsoluteHttp(href: string): boolean {
  return /^https?:\/\//i.test(href) || /^\/\//.test(href);
}

function isAllowedHost(href: string, hosts: ReadonlyArray<string>): boolean {
  try {
    const url = new URL(href.startsWith('//') ? `https:${href}` : href);
    return hosts.some((h) => url.hostname === h || url.hostname.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

interface PendingReplacement {
  start: number;
  end: number;
  replacement: string;
}

function escapeStyleContent(css: string): string {
  // Defensive: a stray `</style>` inside a fetched stylesheet would terminate our
  // `<style>` block early. The browser swallows escaped sequences inside <style>
  // since it parses raw text up to `</style>`. Replace with a benign sequence.
  return css.replace(/<\/style>/gi, '<\\/style>');
}

function escapeScriptContent(js: string): string {
  // Same hazard as <style>: a literal `</script>` inside a fetched bundle would
  // terminate our inline block early. Backslash-escape the slash so the JS parser
  // sees it the same way but the HTML parser doesn't.
  return js.replace(/<\/script>/gi, '<\\/script>');
}

export async function selfContainHtml(
  html: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SelfContainResult> {
  const inlined: string[] = [];
  const stripped: SelfContainResult['stripped'] = [];
  const kept: string[] = [];
  const replacements: PendingReplacement[] = [];

  // Pass 1: find all <link> tags that look like external stylesheets.
  const linkJobs: Array<{
    href: string;
    start: number;
    end: number;
  }> = [];
  for (const m of html.matchAll(LINK_TAG_RE)) {
    const idx = m.index ?? 0;
    const attrs = parseAttrs(m[1] ?? '');
    const rel = (attrs.rel ?? '').toLowerCase();
    if (!/\bstylesheet\b/.test(rel)) continue;
    const href = attrs.href ?? '';
    if (!href) continue;
    linkJobs.push({ href, start: idx, end: idx + m[0].length });
  }

  // Process link jobs (parallel fetches where needed).
  await Promise.all(
    linkJobs.map(async (job) => {
      const { href, start, end } = job;
      if (!isAbsoluteHttp(href)) {
        replacements.push({ start, end, replacement: '' });
        stripped.push({
          kind: 'link',
          href,
          reason: 'Relative path — cannot resolve in sandbox',
        });
        return;
      }
      if (isAllowedHost(href, ALLOWED_STYLESHEET_HOSTS)) {
        kept.push(href);
        return;
      }
      try {
        const url = href.startsWith('//') ? `https:${href}` : href;
        const response = await fetchImpl(url, { mode: 'cors' });
        if (!response.ok) {
          replacements.push({ start, end, replacement: '' });
          stripped.push({ kind: 'link', href, reason: `HTTP ${response.status}` });
          return;
        }
        const css = await response.text();
        const styleTag = `<style data-cdc-inlined-from="${href.replace(/"/g, '&quot;')}">${escapeStyleContent(css)}</style>`;
        replacements.push({ start, end, replacement: styleTag });
        inlined.push(href);
      } catch (err) {
        replacements.push({ start, end, replacement: '' });
        stripped.push({
          kind: 'link',
          href,
          reason: err instanceof Error ? err.message : 'Fetch failed (CORS?)',
        });
      }
    }),
  );

  // Pass 2: external scripts.
  //   - Tailwind CDN: kept as <script src> (CSP-allowlisted).
  //   - Trusted public-asset CDNs (three.js etc.): fetch + inline as <script>.
  //   - Everything else (analytics, trackers, Cloudflare auto-injected): strip.
  const scriptJobs: Array<{
    src: string;
    start: number;
    end: number;
    inlineAttrs: string;
  }> = [];
  function captureScript(match: RegExpMatchArray, idx: number, fullTagLen: number): void {
    const attrs = parseAttrs(match[1] ?? '');
    const src = attrs.src ?? '';
    if (!src) return; // inline <script>...</script> blocks: leave alone
    if (isAllowedHost(src, CSP_ALLOWED_SCRIPT_HOSTS)) {
      kept.push(src);
      return;
    }
    if (isAbsoluteHttp(src) && isAllowedHost(src, INLINEABLE_SCRIPT_HOSTS)) {
      // Defer to async inline pass. Carry forward attrs that affect execution.
      const rawAttrs = match[1] ?? '';
      const passthroughAttrs: string[] = [];
      if (attrs.type && attrs.type !== 'text/javascript') {
        passthroughAttrs.push(`type="${attrs.type.replace(/"/g, '&quot;')}"`);
      }
      // `defer` / `async` are HTML boolean attributes — `parseAttrs` only matches
      // `name="value"` pairs, so check raw attr text for the bare keyword.
      if (/\bdefer\b/i.test(rawAttrs)) passthroughAttrs.push('defer');
      if (/\basync\b/i.test(rawAttrs)) passthroughAttrs.push('async');
      scriptJobs.push({
        src,
        start: idx,
        end: idx + fullTagLen,
        inlineAttrs: passthroughAttrs.length > 0 ? ` ${passthroughAttrs.join(' ')}` : '',
      });
      return;
    }
    replacements.push({ start: idx, end: idx + fullTagLen, replacement: '' });
    stripped.push({
      kind: 'script',
      href: src,
      reason: isAbsoluteHttp(src)
        ? 'External script not on trusted-CDN allowlist'
        : 'Relative path — cannot resolve in sandbox',
    });
  }

  for (const m of html.matchAll(SCRIPT_TAG_RE)) {
    captureScript(m, m.index ?? 0, m[0].length);
  }
  for (const m of html.matchAll(SCRIPT_SELF_CLOSING_RE)) {
    captureScript(m, m.index ?? 0, m[0].length);
  }

  await Promise.all(
    scriptJobs.map(async (job) => {
      try {
        const url = job.src.startsWith('//') ? `https:${job.src}` : job.src;
        const response = await fetchImpl(url, { mode: 'cors' });
        if (!response.ok) {
          replacements.push({ start: job.start, end: job.end, replacement: '' });
          stripped.push({ kind: 'script', href: job.src, reason: `HTTP ${response.status}` });
          return;
        }
        const body = await response.text();
        const safeSrc = job.src.replace(/"/g, '&quot;');
        const inlineTag = `<script${job.inlineAttrs} data-cdc-inlined-from="${safeSrc}">${escapeScriptContent(body)}</script>`;
        replacements.push({ start: job.start, end: job.end, replacement: inlineTag });
        inlined.push(job.src);
      } catch (err) {
        replacements.push({ start: job.start, end: job.end, replacement: '' });
        stripped.push({
          kind: 'script',
          href: job.src,
          reason: err instanceof Error ? err.message : 'Fetch failed (CORS?)',
        });
      }
    }),
  );

  // Apply replacements right-to-left so positions stay stable.
  replacements.sort((a, b) => b.start - a.start);
  let out = html;
  for (const r of replacements) {
    out = out.slice(0, r.start) + r.replacement + out.slice(r.end);
  }

  return { html: out, inlined, stripped, kept };
}
